import type { JSX, ReactNode } from 'react';

import { Box, Stack, Typography, useTheme } from '@mui/material';

// Vapas empty-state / error pattern (DESIGN.md §8.9 + §18.3): a large icon in a
// beige (`background.default`) circle, an optional uppercase status code, an
// H2 heading, a two-line explanation, and one or more action buttons. Shared
// by NotFoundPage / ForbiddenPage (and reusable for empty tables / sections).
// Copy is passed pre-translated by the caller (the view owns its i18n keys).

const CIRCLE_SIZE_PX = 80;

export interface ErrorStateProps {
  icon: ReactNode;
  // Optional uppercase eyebrow (e.g. "404" / "403").
  code?: string;
  title: string;
  description: string;
  actions?: ReactNode;
  'data-testid'?: string;
}

export const ErrorState = ({
  icon,
  code,
  title,
  description,
  actions,
  'data-testid': dataTestId,
}: ErrorStateProps): JSX.Element => {
  const theme = useTheme();
  return (
    <Box
      data-testid={dataTestId}
      sx={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: theme.spacing(4),
        width: '100%',
      }}
    >
      <Stack spacing={2} alignItems="center" sx={{ maxWidth: 480, textAlign: 'center' }}>
        <Box
          aria-hidden
          sx={{
            width: CIRCLE_SIZE_PX,
            height: CIRCLE_SIZE_PX,
            borderRadius: '50%',
            bgcolor: theme.palette.background.default,
            border: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: theme.palette.text.secondary,
            '& svg': { fontSize: 36 },
          }}
        >
          {icon}
        </Box>
        {code ? (
          <Typography
            component="span"
            sx={{
              fontSize: 11.5,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              color: theme.palette.text.secondary,
              fontFamily: theme.monospaceFontFamily,
            }}
          >
            {code}
          </Typography>
        ) : null}
        <Typography
          component="h2"
          sx={{ fontSize: 20, fontWeight: 600, letterSpacing: '-0.01em', color: theme.palette.text.primary }}
        >
          {title}
        </Typography>
        <Typography sx={{ fontSize: 13.5, lineHeight: 1.6, color: theme.palette.text.secondary }}>
          {description}
        </Typography>
        {actions ? (
          <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
            {actions}
          </Stack>
        ) : null}
      </Stack>
    </Box>
  );
};
